/**
 * iter-230k — Lightweight header used across onboarding pages.
 *
 * Provides a clear path to sign out if a vendor lands here with the
 * wrong account / wants to switch users. Without this, the only way to
 * leave the onboarding flow was to type a URL or hit logout via the
 * browser dev tools — invisible to most users.
 *
 * Used by:
 *   - VendorTaxRegistration
 *   - OnboardingQuestions
 *   - (any future onboarding page that doesn't render the main app
 *     sidebar)
 *
 * Stays optional via props so individual pages can hide the brand or
 * show their own step indicator above/below.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const OnboardingHeader = ({
  showBrand = true,
  subtitle = null,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      if (typeof logout === 'function') {
        await logout();
      }
    } finally {
      // Hard redirect to /login so any in-memory state is cleared and
      // the next visitor on this device sees a clean slate.
      navigate('/login', { replace: true });
    }
  };

  return (
    <header
      className="w-full bg-white border-b border-gray-200"
      data-testid="onboarding-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {showBrand && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-600 flex items-center justify-center text-white font-bold text-sm">
                A
              </div>
              <span className="font-semibold text-gray-900">Aniyor</span>
            </div>
          )}
          {subtitle && (
            <span className="text-sm text-gray-500 hidden sm:inline truncate">
              {subtitle}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {user?.email && (
            <span
              className="text-xs text-gray-500 hidden md:inline truncate max-w-[200px]"
              data-testid="onboarding-header-email"
              title={user.email}
            >
              {user.email}
            </span>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 hover:text-red-600 hover:border-red-200 transition-colors"
            data-testid="onboarding-signout-btn"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default OnboardingHeader;
